import { useCallback, useEffect, useState } from 'react'
import { Search, Shield, X } from 'lucide-react'
import { useT } from '@/i18n'
import { fetchAuditActions, searchAudit, type AuditRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { PermissionDenied } from '@/components/States'

function dayStart(v: string): number | undefined {
  if (!v) return undefined
  const t = new Date(`${v}T00:00:00`).getTime()
  return Number.isFinite(t) ? t : undefined
}
function dayEnd(v: string): number | undefined {
  if (!v) return undefined
  const t = new Date(`${v}T23:59:59`).getTime()
  return Number.isFinite(t) ? t : undefined
}
function fmtTime(ts: string | number): string {
  const n = typeof ts === 'number' ? ts : Number(ts)
  if (!Number.isFinite(n)) return '—'
  try {
    return new Date(n).toLocaleString()
  } catch {
    return '—'
  }
}

/**
 * A18.02 — Audit qidiruvi. Server tomonда filtrlangan (`GET /admin/audit/search`):
 * amal/xodim/obyekt/sana bo'yicha BUTUN jurnalni qidiradi (A18.01 faqat yaqin
 * oynani ko'rsatadi). Read-only, AUDIT_VIEW-gated.
 */
export function AuditSearch() {
  const t = useT()
  const { can } = useAuth()
  const [actions, setActions] = useState<string[]>([])
  const [action, setAction] = useState('')
  const [admin, setAdmin] = useState('')
  const [target, setTarget] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')
  const [rows, setRows] = useState<AuditRow[] | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchAuditActions()
      .then(setActions)
      .catch(() => setActions([]))
  }, [])

  const run = useCallback(() => {
    setLoading(true)
    searchAudit({ action, admin, target, since: dayStart(since), until: dayEnd(until), limit: 300 })
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [action, admin, target, since, until])

  const clear = () => {
    setAction('')
    setAdmin('')
    setTarget('')
    setSince('')
    setUntil('')
    setRows(null)
  }

  if (!can(PERMISSIONS.AUDIT_VIEW)) return <PermissionDenied permission={PERMISSIONS.AUDIT_VIEW} />

  const input = 'rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-primary outline-none'

  return (
    <>
      <div className="border-b border-border-subtle p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{t.col.action}</span>
            <select value={action} onChange={(e) => setAction(e.target.value)} className={input}>
              <option value="">{t.auditSearch.anyAction}</option>
              {actions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{t.col.staff}</span>
            <input value={admin} onChange={(e) => setAdmin(e.target.value)} placeholder="username" className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{t.col.target}</span>
            <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="user:123" className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{t.auditSearch.since}</span>
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)} className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{t.auditSearch.until}</span>
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className={input} />
          </label>
          <button onClick={run} disabled={loading} className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
            <Search size={14} />
            {t.auditSearch.search}
          </button>
          <button onClick={clear} className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-secondary hover:text-primary">
            <X size={14} />
            {t.common.clear}
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {rows === null ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">
            <Shield size={24} className="mx-auto mb-2 opacity-60" />
            {t.auditSearch.hint}
          </div>
        ) : loading ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">{t.states.emptyTitle}</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="mb-2 text-xs text-tertiary">{t.auditSearch.results(rows.length)}</div>
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-border-default text-left text-xs text-tertiary">
                  <th className="px-2 py-1.5 font-medium">{t.col.time}</th>
                  <th className="px-2 py-1.5 font-medium">{t.col.staff}</th>
                  <th className="px-2 py-1.5 font-medium">{t.col.action}</th>
                  <th className="px-2 py-1.5 font-medium">{t.col.target}</th>
                  <th className="px-2 py-1.5 font-medium">{t.col.detail}</th>
                  <th className="px-2 py-1.5 font-medium">{t.col.ip}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={String(r.id ?? `${r.created_at}:${r.action}:${r.target ?? ''}`)} className="border-b border-border-subtle align-top">
                    <td className="whitespace-nowrap px-2 py-1.5 text-tertiary">{fmtTime(r.created_at)}</td>
                    <td className="px-2 py-1.5 text-secondary">{r.admin ?? '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-primary">{r.action}</td>
                    <td className="px-2 py-1.5 font-mono text-secondary">{r.target ?? '—'}</td>
                    <td className="max-w-xs truncate px-2 py-1.5 text-secondary" title={r.detail ?? ''}>{r.detail ?? '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-tertiary">{r.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
